import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Zap, RefreshCw, Globe, Users, ArrowRight } from 'lucide-react';

const valueProps = [
  {
    icon: TrendingUp,
    title: 'Unlock new revenue streams',
    description: 'Create unique interactive ad formats that command premium CPMs and open new inventory opportunities.',
    gradient: 'from-emerald-500 to-cyan-500',
  },
  {
    icon: Zap,
    title: 'Speed through creative development',
    description: 'Use pre-coded components to build stunning interactive ads in minutes, not weeks.',
    gradient: 'from-[#635bff] to-blue-500',
  },
  {
    icon: RefreshCw,
    title: 'Improve performance continuously',
    description: 'Make real-time creative edits based on live data to optimize every campaign.',
    gradient: 'from-purple-500 to-pink-500',
  },
  {
    icon: Globe,
    title: 'Traffic seamlessly everywhere',
    description: 'Deliver campaigns across dozens of ad servers without compatibility headaches.',
    gradient: 'from-amber-500 to-orange-500',
  },
];

interface ValuePropositionProps {
  cmsData?: Record<string, unknown>;
}

const ValueProposition: React.FC<ValuePropositionProps> = ({ cmsData }) => {
  const navigate = useNavigate();

  return (
    <section className="py-24 lg:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-slate-900 dark:bg-[#0a0a0f]">
        {/* Grid pattern */}
        <div className="absolute inset-0 grid-pattern opacity-50" />
        
        {/* Gradient orbs */}
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] opacity-30">
          <div className="w-full h-full bg-gradient-to-br from-[#635bff] to-transparent rounded-full blur-3xl" />
        </div>
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] opacity-20">
          <div className="w-full h-full bg-gradient-to-tl from-[#00d4ff] to-transparent rounded-full blur-3xl" />
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-white">
            {(cmsData?.headline as string) || 'Increase Revenue.'}{' '}
            <span className="stripe-gradient-text">
              {(cmsData?.headline_gradient as string) || 'Decrease Complexity.'}
            </span>
          </h2>
          <p className="mt-6 text-lg text-slate-400">
            {(cmsData?.subtitle as string) || 'Everything you need to create, deliver, and optimize interactive advertising at scale.'}
          </p>
        </div>

        {/* Value Props Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {valueProps.map((prop, index) => (
            <div
              key={index}
              className="group relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 stripe-card"
            >
              {/* Icon with gradient background */}
              <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br ${prop.gradient} mb-6 shadow-lg`}>
                <prop.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{prop.title}</h3>
              <p className="text-slate-400 leading-relaxed">{prop.description}</p>
              
              {/* Hover glow effect */}
              <div className={`absolute -inset-px bg-gradient-to-r ${prop.gradient} rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300 -z-10 blur-xl`} />
            </div>
          ))}
        </div>

        {/* Managed Services CTA */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-6 p-8 bg-gradient-to-r from-[#635bff]/20 to-[#00d4ff]/20 rounded-2xl border border-white/10 backdrop-blur-sm">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#635bff] to-[#00d4ff] flex items-center justify-center shadow-lg">
                <Users className="w-7 h-7 text-white" />
              </div>
              <div className="text-left">
                <p className="text-white font-bold text-lg">Don't know where to start?</p>
                <p className="text-slate-400">Adventive Managed Services can take the wheel.</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/platform#managed')}
              className="inline-flex items-center px-6 py-3 text-sm font-semibold text-slate-900 bg-white rounded-full hover:bg-slate-100 transition-all group shadow-lg"
            >
              Learn More
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValueProposition;
